import { useCallback, useEffect, useState } from "react";
import i18n from "@/lib/i18n";

const DISMISS_KEY = "pwa-install-banner-dismissed-until";
const DISMISS_DAYS = 30;

export function getMobileInstallUrl(): string {
  if (typeof window === "undefined") return "/m";
  return `${window.location.origin}/m`;
}

export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const mql = window.matchMedia?.("(display-mode: standalone)");
  return Boolean(mql?.matches) || (window.navigator as any).standalone === true;
}

export function useIsStandalone() {
  const [standalone, setStandalone] = useState(() => isStandalone());
  useEffect(() => {
    const mql = window.matchMedia("(display-mode: standalone)");
    const handler = () => setStandalone(isStandalone());
    mql.addEventListener?.("change", handler);
    return () => mql.removeEventListener?.("change", handler);
  }, []);
  return standalone;
}

export function isBannerDismissed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const until = parseInt(raw, 10);
    if (!Number.isFinite(until)) return false;
    return Date.now() < until;
  } catch {
    return false;
  }
}

export function dismissBanner(days = DISMISS_DAYS) {
  try {
    const until = Date.now() + days * 24 * 60 * 60 * 1000;
    window.localStorage.setItem(DISMISS_KEY, String(until));
    window.dispatchEvent(new CustomEvent("pwa-banner-dismiss-change"));
  } catch {}
}

export function resetBannerDismissal() {
  try {
    window.localStorage.removeItem(DISMISS_KEY);
    window.dispatchEvent(new CustomEvent("pwa-banner-dismiss-change"));
  } catch {}
}

export function useBannerDismissed() {
  const [dismissed, setDismissed] = useState(() => isBannerDismissed());
  useEffect(() => {
    const handler = () => setDismissed(isBannerDismissed());
    window.addEventListener("pwa-banner-dismiss-change", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("pwa-banner-dismiss-change", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);
  return dismissed;
}

export function useQrDataUrl(text: string, size = 192, enabled = true) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled) {
      setDataUrl(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const QR = (await import("qrcode")).default;
        const url = await QR.toDataURL(text, {
          width: size,
          margin: 1,
          color: { dark: "#0f172a", light: "#ffffff" },
        });
        if (!cancelled) setDataUrl(url);
      } catch {
        if (!cancelled) setDataUrl(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [text, size, enabled]);
  return dataUrl;
}

export type PwaPageContext = {
  match: boolean;
  title: string;
  message: string;
};

const ROUTE_MESSAGES: Array<{
  prefix: string;
  titleKey: string;
  messageKey: string;
}> = [
  {
    prefix: "/dashboard",
    titleKey: "seg14.routeDashboardTitle",
    messageKey: "seg14.routeDashboardMsg",
  },
  {
    prefix: "/orders",
    titleKey: "seg14.routeOrdersTitle",
    messageKey: "seg14.routeOrdersMsg",
  },
  {
    prefix: "/transfers",
    titleKey: "seg14.routeTransfersTitle",
    messageKey: "seg14.routeTransfersMsg",
  },
  {
    prefix: "/purchase-orders",
    titleKey: "seg14.routePurchaseOrdersTitle",
    messageKey: "seg14.routePurchaseOrdersMsg",
  },
  {
    prefix: "/supplier-receipts",
    titleKey: "seg14.routeSupplierReceiptsTitle",
    messageKey: "seg14.routeSupplierReceiptsMsg",
  },
  {
    prefix: "/inventory/returns",
    titleKey: "seg14.routeReturnsTitle",
    messageKey: "seg14.routeReturnsMsg",
  },
  {
    prefix: "/stocktakes",
    titleKey: "seg14.routeStocktakesTitle",
    messageKey: "seg14.routeStocktakesMsg",
  },
  {
    prefix: "/inventory/waste",
    titleKey: "seg14.routeWasteTitle",
    messageKey: "seg14.routeWasteMsg",
  },
  {
    prefix: "/inventory",
    titleKey: "seg14.routeInventoryTitle",
    messageKey: "seg14.routeInventoryMsg",
  },
  {
    prefix: "/branch-inventory",
    titleKey: "seg14.routeBranchInventoryTitle",
    messageKey: "seg14.routeBranchInventoryMsg",
  },
  {
    prefix: "/catalog",
    titleKey: "seg14.routeCatalogTitle",
    messageKey: "seg14.routeCatalogMsg",
  },
  {
    prefix: "/notifications",
    titleKey: "seg14.routeNotificationsTitle",
    messageKey: "seg14.routeNotificationsMsg",
  },
  {
    prefix: "/branch-profile",
    titleKey: "seg14.routeBranchProfileTitle",
    messageKey: "seg14.routeBranchProfileMsg",
  },
  {
    prefix: "/logistics",
    titleKey: "seg14.routeLogisticsTitle",
    messageKey: "seg14.routeLogisticsMsg",
  },
];

export function getPageContext(pathname: string): PwaPageContext {
  const hit = ROUTE_MESSAGES.find((r) => pathname.startsWith(r.prefix));
  if (!hit) return { match: false, title: "", message: "" };
  return { match: true, title: i18n.t(hit.titleKey), message: i18n.t(hit.messageKey) };
}

export function useDeferredInstallPrompt() {
  const [event, setEvent] = useState<any>(null);
  useEffect(() => {
    const handler = (e: any) => {
      e.preventDefault();
      setEvent(e);
    };
    window.addEventListener("beforeinstallprompt", handler as any);
    const installed = () => setEvent(null);
    window.addEventListener("appinstalled", installed);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler as any);
      window.removeEventListener("appinstalled", installed);
    };
  }, []);
  const prompt = useCallback(async () => {
    if (!event) return null;
    event.prompt();
    const choice = await event.userChoice;
    setEvent(null);
    return choice?.outcome ?? null;
  }, [event]);
  return { canInstall: Boolean(event), prompt };
}
